import React from 'react';
import { Typography } from '../../design-system';
import {
  SwHeightComparison,
  SwHeightIssue,
  SwHeightIssueSeverity,
  SwHeightValidation
} from '../../@types/swHeight/types';
import styles from './SwHeightValidationPanel.module.css';

export interface SwHeightValidationPanelProps {
  validation: SwHeightValidation;
  comparison: SwHeightComparison;
}

const SEVERITY_ORDER: SwHeightIssueSeverity[] = [
  'blocking',
  'warning',
  'info'
];

const SEVERITY_LABEL: Record<SwHeightIssueSeverity, string> = {
  blocking: 'Blocking',
  warning: 'Warnings',
  info: 'Notes'
};

const SEVERITY_HINT: Record<SwHeightIssueSeverity, string> = {
  blocking: 'Sw cannot be calculated until these are resolved.',
  warning:
    'Some rows are degraded, or the fitted tendencies are being used outside the range they were calibrated on.',
  info: 'Rules that fired legitimately; no action needed.'
};

const formatNumber = (value: number | null, digits = 4) =>
  value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : value.toFixed(digits);

const formatRows = (issue: SwHeightIssue) => {
  if (!issue.row_count) {
    return null;
  }
  const shown = issue.example_rows.join(', ');
  const more = issue.row_count - issue.example_rows.length;
  return more > 0 ? `rows ${shown} and ${more} more` : `rows ${shown}`;
};

export const SwHeightValidationPanel: React.FC<
  SwHeightValidationPanelProps
> = ({ validation, comparison }) => {
  const grouped = SEVERITY_ORDER.map((severity) => ({
    severity,
    issues: validation.issues.filter((issue) => issue.severity === severity)
  })).filter((group) => group.issues.length > 0);

  const coverage = validation.rows_total
    ? Math.round((validation.rows_with_sw / validation.rows_total) * 100)
    : 0;

  return (
    <div className={styles.panel} data-testid="sw-height-validation">
      <section className={styles.metrics}>
        <Typography.Heading level={4}>Calibration quality</Typography.Heading>
        <p className={styles.metricsHint}>
          Calculated Sw compared against {comparison.reference_curve ?? 'the measured curve'} over
          rows where both values exist.
        </p>
        <div className={styles.metricGrid}>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>Rows compared</span>
            <span className={styles.metricValue} data-testid="metric-n">
              {comparison.n}
            </span>
          </div>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>Mean abs. error</span>
            <span className={styles.metricValue} data-testid="metric-mae">
              {formatNumber(comparison.mae)}
            </span>
          </div>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>RMSE</span>
            <span className={styles.metricValue} data-testid="metric-rmse">
              {formatNumber(comparison.rmse)}
            </span>
          </div>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>Bias</span>
            <span className={styles.metricValue} data-testid="metric-bias">
              {formatNumber(comparison.bias)}
            </span>
            <span className={styles.metricNote}>
              {comparison.bias === null
                ? ''
                : comparison.bias > 0
                ? 'model too wet'
                : 'model too dry'}
            </span>
          </div>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>R²</span>
            <span className={styles.metricValue} data-testid="metric-r2">
              {formatNumber(comparison.r_squared, 3)}
            </span>
          </div>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>Sw coverage</span>
            <span className={styles.metricValue} data-testid="metric-coverage">
              {validation.rows_with_sw} / {validation.rows_total} ({coverage}%)
            </span>
          </div>
        </div>
      </section>

      <section className={styles.issues}>
        <Typography.Heading level={4}>Findings</Typography.Heading>
        {grouped.length === 0 ? (
          <p className={styles.clean} data-testid="validation-clean">
            No issues found. Every row produced a saturation value.
          </p>
        ) : (
          grouped.map((group) => (
            <div key={group.severity} className={styles.group}>
              <h5 className={styles.groupTitle}>
                <span
                  className={`${styles.badge} ${styles[group.severity]}`}
                  data-testid={`severity-${group.severity}`}
                >
                  {SEVERITY_LABEL[group.severity]}
                </span>
                <span className={styles.groupCount}>
                  {group.issues.length}
                </span>
              </h5>
              <p className={styles.groupHint}>
                {SEVERITY_HINT[group.severity]}
              </p>
              <ul className={styles.issueList}>
                {group.issues.map((issue) => (
                  <li
                    key={issue.code}
                    className={styles.issue}
                    data-testid={`issue-${issue.code}`}
                  >
                    <span className={styles.issueMessage}>
                      {issue.message}
                    </span>
                    {formatRows(issue) && (
                      <span className={styles.issueRows}>
                        {issue.row_count} affected — {formatRows(issue)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </section>
    </div>
  );
};

export default SwHeightValidationPanel;
